import { __decorate, __metadata } from "tslib";
import { Serialize } from '@ftr/serialization';
import { Duration, LocalTime } from '@js-joda/core';
import { LocalDateTimeRange } from './LocalDateTimeRange';
import { Timecode } from './Timecode';
export const SECONDS_IN_A_DAY = 24 * 60 * 60;
export const NANOS_IN_A_SECOND = 1000000000;
export const ONE_NANO_BEFORE_MIDNIGHT = LocalTime.MIDNIGHT.minusNanos(1);
/**
 * Represents a LocalTime time range. Contains two LocalTime instances, one that
 * specifies the start of the range and another that specifies the end of the range.
 */
export class LocalTimeRange {
    /**
     * @param {LocalTime} start of the time range.
     * @param {LocalTime} end of the time range.
     */
    constructor(start, end) {
        this.start = start;
        this.end = end;
    }
    static parse(start, end) {
        return new LocalTimeRange(LocalTime.parse(start), LocalTime.parse(end));
    }
    /**
     * Guards against invalid seconds of day aka 86400
     */
    static fromSecondOfDay(start, end) {
        const endTime = end === SECONDS_IN_A_DAY ? ONE_NANO_BEFORE_MIDNIGHT : LocalTime.ofSecondOfDay(end);
        const startTime = LocalTime.ofSecondOfDay(start);
        return new LocalTimeRange(startTime, endTime);
    }
    /**
     * Warning, this is an exclusive end
     */
    static fromPostgresInt4Range(intRange) {
        const results = intRange
            .substring(1, intRange.length - 1)
            .split(',')
            .map(x => Number.parseInt(x, 10));
        return LocalTimeRange.fromSecondOfDay(results[0], results[1]);
    }
    /**
     * Returns the range with the earliest start date and time from the given array.
     * @throws Error if array is empty
     */
    static getEarliest(ranges) {
        if (!ranges.length) {
            throw new Error('Empty array');
        }
        return [...ranges].sort((a, b) => a.start.compareTo(b.start)).shift();
    }
    /**
     * Returns the range with the latest end date and time from the given array.
     * @throws Error if array is empty
     */
    static getLatest(ranges) {
        if (!ranges.length) {
            throw new Error('Empty array');
        }
        return [...ranges].sort((a, b) => a.end.compareTo(b.end)).pop();
    }
    get duration() {
        return Duration.between(this.start, this.end);
    }
    /**
     * Returns `true` if this range contains the given date-time.
     *
     * This is inclusive of start only.
     */
    contains(other) {
        return this.start.compareTo(other) <= 0 && other.compareTo(this.end) < 0;
    }
    /**
     * Returns `true` if this range encloses the given range.
     *
     * This is inclusive of start and end.
     */
    encloses(other) {
        return this.start.compareTo(other.start) <= 0 && other.end.compareTo(this.end) <= 0;
    }
    /**
     * Returns `true` if this range is equal to the given range.
     */
    equals(other) {
        return this.start.equals(other.start) && this.end.equals(other.end);
    }
    /**
     * Returns `true` if this range overlaps the given range.
     */
    overlaps(other) {
        return other.equals(this) || (this.start.compareTo(other.end) < 0 && other.start.compareTo(this.end) < 0);
    }
    toString() {
        return `${this.start.toString()} -> ${this.end.toString()}`;
    }
    /**
     * Postgres uses exclusive end times, this means we have to round UP to the nearest second, given a fractional time.
     */
    toPostgresInt4Range() {
        return `[${this.start.toSecondOfDay()},${this.endSecondOfDayExclusive()})`;
    }
    /**
     * Can be used for auditing and/or postgres int4range types
     */
    endSecondOfDayExclusive() {
        return Math.ceil(this.end.toNanoOfDay() / NANOS_IN_A_SECOND);
    }
    toTimecode(date) {
        return new Timecode(this.start.atDate(date), this.duration.toMillis());
    }
    atDate(date) {
        return LocalDateTimeRange.of(date, this.start, this.end);
    }
}
LocalTimeRange.FULL_DAY = new LocalTimeRange(LocalTime.MIDNIGHT, LocalTime.MIDNIGHT.minusNanos(1));
__decorate([
    Serialize(),
    __metadata("design:type", LocalTime)
], LocalTimeRange.prototype, "start", void 0);
__decorate([
    Serialize(),
    __metadata("design:type", LocalTime
    /**
     * @param {LocalTime} start of the time range.
     * @param {LocalTime} end of the time range.
     */
    )
], LocalTimeRange.prototype, "end", void 0);
